import { useEffect } from "react";
import ReactDom from "react-dom/client";
import { BrowserRouter, useLocation } from "react-router-dom";
import { Provider } from "react-redux";
// import { createBrowserHistory } from "history";
import store from "./redux";
import Routes from "./routes";
//Import the global styles
import "./lib/api";
import "./lib/i18n";
import "./assets/styles/tailwind.generated.css";
import "./assets/styles/config.scss";
import "./assets/styles/template.scss";
import "./assets/styles/typography.scss";
import AlertContainer from "./components/AlertContainer";
import useAnalytics from "./hooks/useAnalytics";
import facebookInit from "./lib/facebookInit";
import useApiSockets from "./hooks/sockets/useApiSockets";
import useRedirect from "./hooks/useRedirect";
import withClearCache from "./lib/withClearCache";
import Head from "src/components/Head";
import useTokenListener from "src/hooks/useTokenListener";
import { useThemeListener } from "src/hooks/useTheme";
import useLatestBuild from "src/hooks/useCurrentVersion";
import DebugTools from "src/components/_debug/DebugTools";
import useNotificationAnalytics from "src/hooks/useNotificationAnalytics";
import MaintenanceView from "src/views/global/MaintenanceView";
import TenantProvider from "src/components/providers/TenantProvider";
import useAuth from "src/hooks/selectors/useAuth";
import TaskModal from "src/components/forms/TaskForm/modals/task-modal/TaskModal";
import TaskFormTitleModal from "src/components/forms/TaskForm/modals/TaskFormTitleModal";
import useSubscription from "src/hooks/selectors/useSubscription";
import ListenerProvider from "src/components/providers/ListenerProvider";
import SubscriptionProtectModal from "src/components/subscription/subscription-protect/SubscriptionProtectModal";

const IS_MAINTENANCE_MODE = process.env.REACT_APP_MAINTENANCE_MODE === "true";

declare global {
	interface Window {
		google: any;
		fbAsyncInit: any;
		FB: any;
		dataLayer: any;
	}
}
if (process.env.NODE_ENV !== "development") {
	facebookInit();
}

const TokenListener = () => {
	useTokenListener();
	return <></>;
};
const AnalyticsProvider = () => {
	useAnalytics();
	useRedirect();
	useNotificationAnalytics();
	return <></>;
};

const ModalListener = () => {
	const location = useLocation();
	useEffect(() => {
		const body = document.querySelector("body");
		const modal = document.querySelector("#modal");
		if (!modal) {
			body?.classList?.remove("overflow-hidden");
		}
	}, [location]);
	return <></>;
};

const AppRoutes = () => {
	useThemeListener();
	useApiSockets();
	const build = useLatestBuild();
	const auth = useAuth();
	const subscription = useSubscription();
	useEffect(() => {
		if (process.env.NODE_ENV !== "development") {
			console.log(`Build version ${build}`);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<main className="bg-gray-background dark:bg-dark flex flex-col flex-1">
			<BrowserRouter>
				<Routes>
					{auth?.type === "company" && (
						<>
							<TaskModal />
							<SubscriptionProtectModal />
							{subscription?.id && <TaskFormTitleModal />}
						</>
					)}
					{auth?.id && <ListenerProvider />}
					<AnalyticsProvider />
					<AlertContainer />
					<TokenListener />
					<DebugTools />
					<ModalListener />
				</Routes>
			</BrowserRouter>
		</main>
	);
};

const App = () => {
	return (
		<TenantProvider>
			<AppRoutes />
		</TenantProvider>
	);
};

const ClearedCacheApp = withClearCache(
	IS_MAINTENANCE_MODE ? MaintenanceView : App
);

const element = document.getElementById("root");
if (element) {
	const root = ReactDom.createRoot(element);

	root.render(
		<Provider store={store}>
			<>
				<Head />

				{process.env.NODE_ENV === "production" ? (
					<ClearedCacheApp />
				) : IS_MAINTENANCE_MODE ? (
					<MaintenanceView />
				) : (
					<App />
				)}
			</>
		</Provider>
	);
}

// ReactDOM.render(
// 	<React.StrictMode></React.StrictMode>,
// 	document.getElementById("root")
// );
